'use client'

import React, { useEffect } from 'react'
import { Search, Loader2, AlertCircle } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import TerpeneListItem from './TerpeneListItem'
import TerpeneDetailDisplay from './TerpeneDetailDisplay'
import TerpeneFilters from './TerpeneFilters'
import { useTerpeneStore } from '@/stores/terpeneStore'

export default function TerpeneBrowserSection() {
  const {
    terpenes,
    filteredTerpenes,
    selectedTerpene,
    searchQuery,
    selectedCategory,
    selectedEffects,
    selectedSources,
    isLoading,
    error,
    fetchTerpenes,
    fetchFilterOptions,
    setSearchQuery,
    setSelectedCategory,
    setSelectedEffects,
    setSelectedSources,
    selectTerpene,
    clearFilters
  } = useTerpeneStore()

  // Fetch terpenes on component mount
  useEffect(() => {
    if (terpenes.length === 0) {
      fetchTerpenes()
      // fetchFilterOptions is now called automatically after fetchTerpenes
    }
  }, [])

  if (isLoading && terpenes.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-400 mx-auto mb-2" />
          <p className="text-white/60">Loading terpenes...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <Alert className="bg-red-500/10 border border-red-500/20 max-w-md">
          <AlertCircle className="h-4 w-4 text-red-400" />
          <AlertDescription className="text-red-300">
            {error}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 lg:gap-6 w-full overflow-hidden">
      {/* Left Panel - Terpene List with Search and Filters */}
      <div className="flex flex-col min-w-0">
        {/* Header with Search */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h3 className="text-lg sm:text-xl font-semibold text-hierarchy-1 truncate">
              Browse Terpenes
            </h3>
            {/* Results count */}
            <p className="text-xs sm:text-sm text-white/60 whitespace-nowrap">
              {filteredTerpenes.length} {filteredTerpenes.length === 1 ? 'terpene' : 'terpenes'} found
            </p>
          </div>
          
          <div className="relative pl-1 pr-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              type="text"
              placeholder="Search terpenes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-800/30 border-white/10 text-white placeholder:text-white/40 ring-offset-0 focus:ring-2 focus:ring-emerald-400/60 focus:outline-none"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4">
          <TerpeneFilters
            selectedCategory={selectedCategory}
            selectedEffects={selectedEffects}
            selectedSources={selectedSources}
            onCategoryChange={setSelectedCategory}
            onEffectsChange={setSelectedEffects}
            onSourcesChange={setSelectedSources}
            onClearAll={clearFilters}
          />
        </div>

        {/* Terpenes List - Fixed height with scroll */}
        <div className="max-h-[200px] sm:max-h-[300px] lg:max-h-[400px] scroll-safe space-y-2 pr-2">
          {filteredTerpenes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-white/60">
                {searchQuery || selectedCategory || selectedEffects.length > 0 || selectedSources.length > 0
                  ? 'No terpenes match your filters.'
                  : 'No terpenes available.'}
              </p>
            </div>
          ) : (
            filteredTerpenes.map((terpene) => (
              <TerpeneListItem
                key={terpene.id}
                terpene={terpene}
                isSelected={selectedTerpene?.id === terpene.id}
                onSelect={selectTerpene}
              />
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Terpene Detail Display */}
      <div className="min-w-0 overflow-hidden">
        <TerpeneDetailDisplay selectedTerpene={selectedTerpene} />
      </div>
    </div>
  )
}